import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import useAuthStore from '@/lib/stores/authStore';
import { signupSchema, type SignupFormData } from '@/lib/validations/auth';
import type { FirebaseAuthError } from '@/types';
import { ROUTES } from '@/types';

/**
 * Apple-inspired signup form component
 * Handles user registration with email, password, and name
 */
export const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, loading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    watch,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onBlur', // Validate when user leaves field
    reValidateMode: 'onChange', // Re-validate on change after first validation
  });

  const watchedFields = watch();

  // Track when fields are being actively edited
  const handleFieldFocus = (fieldName: 'name' | 'email' | 'password' | 'confirmPassword') => {
    // Clear errors when user focuses on a field (starts typing)
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
    if (errors.root?.message) {
      clearErrors('root');
    }
    if (error) {
      clearError();
    }
  };

  const onSubmit = async (data: SignupFormData) => {
    try {
      clearError();
      await signUp(data.email, data.password, data.name);
      
      // Navigate directly to restaurants
      navigate(ROUTES.RESTAURANTS);
    } catch (error: unknown) {
      // Clear store error immediately since we're handling it in the form
      clearError();
      
      // Handle specific Firebase errors
      const firebaseError = error as FirebaseAuthError;
      if (firebaseError.code === 'auth/email-already-in-use') {
        setError('email', { 
          message: 'An account with this email already exists' 
        });
      } else if (firebaseError.code === 'auth/weak-password') {
        setError('password', { 
          message: 'Password is too weak' 
        });
      } else if (firebaseError.code === 'auth/invalid-email') {
        setError('email', { 
          message: 'Invalid email address' 
        });
      } else if (firebaseError.code === 'auth/operation-not-allowed') {
        setError('root', { 
          message: 'Account creation is currently disabled. Please contact support.' 
        });
      } else {
        setError('root', { 
          message: firebaseError.message || 'Account creation failed. Please try again.' 
        });
      }
    }
  };

  // Enable button when all fields have values and no current validation errors are showing
  const hasValidFormData = 
    watchedFields.name && 
    watchedFields.email && 
    watchedFields.password && 
    watchedFields.confirmPassword &&
    !errors.name &&
    !errors.email &&
    !errors.password &&
    !errors.confirmPassword;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Form Fields */}
      <div className="space-y-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Input
            {...register('name')}
            type="text"
            label="Full Name"
            placeholder="Your full name"
            leftIcon={<User className="w-4 h-4" />}
            error={errors.name?.message}
            disabled={isSubmitting || loading}
            autoComplete="name"
            autoFocus
            onFocus={() => handleFieldFocus('name')}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Input
            {...register('email')}
            type="email"
            label="Email Address"
            placeholder="your@email.com"
            leftIcon={<Mail className="w-4 h-4" />}
            error={errors.email?.message}
            disabled={isSubmitting || loading}
            autoComplete="email"
            onFocus={() => handleFieldFocus('email')}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Input
            {...register('password')}
            type="password"
            label="Password"
            placeholder="Create a strong password"
            leftIcon={<Lock className="w-4 h-4" />}
            error={errors.password?.message}
            disabled={isSubmitting || loading}
            autoComplete="new-password"
            onFocus={() => handleFieldFocus('password')}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Input
            {...register('confirmPassword')}
            type="password"
            label="Confirm Password"
            placeholder="Confirm your password"
            leftIcon={<Lock className="w-4 h-4" />}
            error={errors.confirmPassword?.message}
            disabled={isSubmitting || loading}
            autoComplete="new-password"
            onFocus={() => handleFieldFocus('confirmPassword')}
          />
        </motion.div>
      </div>

      {/* Error Display - Toast style overlay */}
      <AnimatePresence mode="wait">
        {errors.root?.message && (
          <motion.div
            key="error-message"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-apple-lg"
          >
            <p className="text-sm font-medium text-red-800">
              {errors.root.message}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isSubmitting || loading}
          rightIcon={!isSubmitting && !loading ? <ArrowRight className="w-4 h-4" /> : undefined}
          disabled={!hasValidFormData || isSubmitting}
        >
          {isSubmitting || loading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </motion.div>

      {/* Sign In Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-center pt-4 border-t border-gray-100"
      >
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <Link
            to={ROUTES.SIGNIN}
            className="font-medium text-primary hover:text-primary/80 transition-colors duration-200"
          >
            Sign in here
          </Link>
        </p>
      </motion.div>
    </form>
  );
};

export default SignupForm;